// TelcoTrack — D5 occupancy coordinate helpers. Never mix numbering layers.

import type { RozvadecCassetteAdapterFamily } from './rozvadec-cassette.constants';
import {
  formatRozvadecKm5ConnectorLabel,
  rozvadecKm5AdapterPosition,
  rozvadecKm5ConnectorPolarity,
} from './rozvadec-cassette.constants';
import {
  getRozvadecCabinetType,
  getRozvadecMaterialIdentity,
  resolveRozvadecCabinetTypeByAlias,
  resolveRozvadecPersistableTypeFromMaterials,
  type RozvadecCabinetTypeId,
} from './rozvadec-cabinet.constants';
import type { RozvadecOccupancyCabinetCode } from './rozvadec-occupancy-schema.types';

const CABINET_CODE_PRINT: Record<RozvadecOccupancyCabinetCode, string> = {
  MUK24: 'MUK24',
  MUK48: 'MUK48',
  PON32: 'PON32',
  RAYTR3: 'RAYTR3',
  OTHER: 'CAB',
};

const CASSETTE_TOKEN: Record<string, string> = {
  KM4: 'KM4',
  KM5_SC: 'KM5-SC',
  KM5_LC: 'KM5-LC',
  KM5_SPLITTER_1X16: 'KM5-SPL16',
  KM5_SPLITTER_2X8: 'KM5-SPL28',
  KM5_SPLITTER_1X4: 'KM5-SPL14',
  KM5_SPLITTER_2X4: 'KM5-SPL24',
  KM6: 'KM6',
};

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** D5 print aliases — MUK24 is identity-ambiguous but occupancy treats it as 1×24. */
const OCCUPANCY_TYPE_ALIASES: Record<string, RozvadecCabinetTypeId> = {
  muk24: 'MUK_1X24',
  muk124: 'MUK_1X24',
  muk1x24: 'MUK_1X24',
  muk48: 'MUK_2X24',
  muk224: 'MUK_2X24',
  muk2x24: 'MUK_2X24',
  raytr3: 'RSU_ODF',
  rsuodf: 'RSU_ODF',
  rsu: 'RSU_ODF',
};

function occupancyTypeAliasKey(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[×✕✖⨯]/g, 'x')
    .replace(/[^a-z0-9]+/g, '');
}

export function resolveOccupancyCabinetTypeId(
  typeId: string | null | undefined,
  extras?: {
    derivedFromSap?: string | null;
    materials?: ReadonlyArray<{ sapCode?: string | null }> | null;
    rsuOccupancy?: { modules?: readonly unknown[] } | null;
  },
): RozvadecCabinetTypeId | null {
  const direct = getRozvadecCabinetType(typeId);
  if (direct) return direct.id;
  const aliasKey = occupancyTypeAliasKey(typeId ?? '');
  const occupancyAlias = aliasKey ? OCCUPANCY_TYPE_ALIASES[aliasKey] : undefined;
  if (occupancyAlias) return occupancyAlias;
  const catalogAlias = resolveRozvadecCabinetTypeByAlias(typeId);
  if (catalogAlias) return catalogAlias.id;
  const sap = getRozvadecMaterialIdentity(extras?.derivedFromSap);
  if (sap) return sap.typeId;
  const fromMaterials = resolveRozvadecPersistableTypeFromMaterials(extras?.materials);
  if (fromMaterials.status === 'resolved') return fromMaterials.type.id;
  if ((extras?.rsuOccupancy?.modules?.length ?? 0) > 0) return 'RSU_ODF';
  return null;
}

export function occupancyCabinetCodeFromTypeId(
  typeId: string | null | undefined,
): RozvadecOccupancyCabinetCode {
  const resolved = resolveOccupancyCabinetTypeId(typeId) ?? (typeId ?? '').trim();
  if (resolved === 'MUK_1X24') return 'MUK24';
  if (resolved === 'MUK_2X24') return 'MUK48';
  if (resolved === 'PON32') return 'PON32';
  if (resolved === 'RSU_ODF') return 'RAYTR3';
  return 'OTHER';
}

export function occupancyRowToken(rowId: string, rowIndex: number): string {
  if (rowId === 'rada_1' || rowId === 'rack') return 'R1';
  if (rowId === 'rada_2') return 'R2';
  return `R${rowIndex + 1}`;
}

/** Cabinet slot layer: `MUK48:R1:P13`. */
export function occupancyCabinetPositionId(input: {
  cabinetCode: RozvadecOccupancyCabinetCode;
  rowToken: string;
  position: number;
}): string {
  return `${CABINET_CODE_PRINT[input.cabinetCode]}:${input.rowToken}:P${pad2(input.position)}`;
}

/** Adapter / connector layer: `MUK48:R1:P13:KM5-LC:AD05:b`. */
export function occupancyConnectorId(input: {
  cabinetCode: RozvadecOccupancyCabinetCode;
  rowToken: string;
  startPosition: number;
  cassetteType: string;
  adapterFamily: RozvadecCassetteAdapterFamily;
  portOrdinal: number;
}): string {
  const adapter = rozvadecKm5AdapterPosition(input.adapterFamily, input.portOrdinal);
  const polarity = rozvadecKm5ConnectorPolarity(input.adapterFamily, input.portOrdinal);
  const token = CASSETTE_TOKEN[input.cassetteType] ?? input.cassetteType;
  const base = `${occupancyCabinetPositionId({
    cabinetCode: input.cabinetCode,
    rowToken: input.rowToken,
    position: input.startPosition,
  })}:${token}:AD${pad2(adapter ?? 0)}`;
  return polarity ? `${base}:${polarity}` : base;
}

/** Splice seat layer: `PON32:KM6-2:S07` or `MUK24:R1:P19:KM4:S17`. */
export function occupancySpliceId(input: {
  cabinetCode: RozvadecOccupancyCabinetCode;
  rowToken: string | null;
  startPosition: number | null;
  cassetteType: string;
  cassetteOrdinal: number;
  seat: number;
}): string {
  const token = CASSETTE_TOKEN[input.cassetteType] ?? input.cassetteType;
  if (input.cabinetCode === 'PON32') {
    return `PON32:${token}-${input.cassetteOrdinal}:S${pad2(input.seat)}`;
  }
  const position = input.startPosition ?? 0;
  const row = input.rowToken ?? 'R1';
  return `${CABINET_CODE_PRINT[input.cabinetCode]}:${row}:P${pad2(position)}:${token}:S${pad2(input.seat)}`;
}

export function occupancySplitterId(input: {
  cassetteId: string | null;
  splitterIndex: number;
}): string {
  const prefix = input.cassetteId ? input.cassetteId : 'loose';
  return `${prefix}:SPL-${pad2(input.splitterIndex)}`;
}

export function occupancySplitterPortLabel(input: {
  splitterIndex: number;
  direction: 'in' | 'out';
  port: number;
}): string {
  return input.direction === 'in'
    ? `IN ${input.splitterIndex}/${input.port}`
    : `OUT ${input.splitterIndex}/${input.port}`;
}

export function occupancyKm5ConnectorPrintLabel(
  adapterFamily: RozvadecCassetteAdapterFamily,
  portOrdinal: number,
): string {
  return formatRozvadecKm5ConnectorLabel(adapterFamily, portOrdinal) ?? `${adapterFamily}-${portOrdinal}`;
}

/**
 * Preinstalled KM5 splitter outputs: odd on start/upper, even on start+1.
 * D5 row number is the cabinet position, not the splitter output number.
 */
export function occupancySplitterParityPosition(startPosition: number, output: number): number {
  return output % 2 === 1 ? startPosition : startPosition + 1;
}

export function occupancyMukRowPrintLabel(rowLabel: string, position: number): string {
  return `${rowLabel} / pozice ${position}`;
}
